import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';

/**
 * TanStack Query hook for the suggested-next-invoice-number helper
 * (`GET /api/v1/invoices/next-number`, plan 27.1-01). Fired once on the create
 * form's mount to pre-fill the (fully editable) invoice-number field.
 *
 * This is a DASHBOARD-ONLY convenience read — no `@hbs-us/contracts` mirror, no
 * mobile consumer. The suggestion is NEVER authoritative: the server still
 * generates/validates a unique, tenant-prefixed number on save regardless of
 * what (if anything) this query returns, so a failed or slow fetch here must
 * never block the form — callers degrade to a blank field on error.
 *
 * Every request goes through `apiFetch` (the only fetch path — `credentials:
 * 'include'` + `X-Tenant-Subdomain` baked in). The tenant is derived
 * server-side from the auth session; the SPA never sends a tenant id param.
 */

export interface NextInvoiceNumberResponse {
  ok: boolean;
  invoiceNumber?: string;
}

export function nextInvoiceNumberQueryKey(): readonly unknown[] {
  return ['invoices', 'next-number'] as const;
}

export function useNextInvoiceNumberQuery(enabled = true) {
  return useQuery<NextInvoiceNumberResponse>({
    queryKey: nextInvoiceNumberQueryKey(),
    queryFn: () => apiFetchJson<NextInvoiceNumberResponse>('/api/v1/invoices/next-number'),
    enabled,
    retry: false,
    // The suggestion goes stale the moment another invoice is created elsewhere
    // (it would collide); always refetch fresh on each create-form mount rather
    // than trusting a cached value.
    staleTime: 0,
  });
}
